import { type Observable } from '@legendapp/state';
import { createStore } from './store.setup';
import {
    ALERT_ANIMATIONS,
    ALERT_POSITIONS,
    ALERT_TYPES,
    type AlertAnimation,
    type AlertPosition,
    type AlertType,
} from '@kidslabs/mario-nextjs-react-ui-core';

// ============================================================================
// FLASH MESSAGE TYPES
// ============================================================================

export interface FlashMessage {
    /** Unique identifier for the message */
    id: string;
    /** Message type/severity */
    type: AlertType;
    /** Message title (optional) */
    title?: string;
    /** Message content */
    message: string;
    /** Auto-dismiss duration in ms (0 = no auto dismiss) */
    duration?: number;
    /** Alert animation duration in ms */
    animationDuration?: number;
    /** Alert position */
    position?: AlertPosition;
    /** Alert animation type */
    animation?: AlertAnimation;
    /** Timestamp when message was created */
    createdAt: number;
}

interface FlashMessageStore {
    messages: FlashMessage[];
}

// ============================================================================
// TRANSIENT STORE - NO persistence, always starts fresh
// ============================================================================

/**
 * Flash message store (non-persistent)
 *
 * Stores flash messages in memory only.
 * Messages are lost on page refresh — this is intentional (like Magento flash messages).
 *
 * ✅ No persistence - resets on page refresh
 * ✅ For temporary UI notifications only
 */
export const flashMessageStore$: Observable<FlashMessageStore> = createStore<FlashMessageStore>({
    messages: [],
});

// ============================================================================
// ACTIONS - CONTROLLED MUTATIONS
// ============================================================================

let messageCounter = 0;

function generateMessageId(): string {
    messageCounter += 1;
    return `flash-${Date.now()}-${messageCounter}`;
}

export const flashMessageStoreActions = {
    /**
     * Add a new flash message to the stack
     */
    addMessage: (params: Omit<FlashMessage, 'id' | 'createdAt'>): string => {
        const id = generateMessageId();
        const message: FlashMessage = {
            ...params,
            id,
            duration: params.duration ?? 0,
            animationDuration: params.animationDuration ?? 500,
            position: params.position ?? ALERT_POSITIONS.TOP_RIGHT,
            animation: params.animation ?? ALERT_ANIMATIONS.FADE,
            createdAt: Date.now(),
        };

        const currentMessages: FlashMessage[] = flashMessageStore$.messages.peek() ?? [];
        flashMessageStore$.messages.set([...currentMessages, message]);
        return id;
    },

    /**
     * Remove a specific flash message by ID
     */
    removeMessage: (id: string): void => {
        const currentMessages: FlashMessage[] = flashMessageStore$.messages.peek() ?? [];
        flashMessageStore$.messages.set(currentMessages.filter((message: FlashMessage) => message.id !== id));
    },

    /**
     * Remove all flash messages
     */
    clearAllMessages: (): void => {
        flashMessageStore$.messages.set([]);
    },

    /**
     * Get all current messages
     */
    getMessages: (): ReadonlyArray<FlashMessage> => {
        return flashMessageStore$.messages.peek() ?? [];
    },

    /**
     * Get messages observable for reactive subscriptions
     */
    getMessagesObservable: () => flashMessageStore$.messages,
} as const;

// ============================================================================
// PUBLIC API - SINGLE IMPORT POINT
// ============================================================================

type FlashMessageCreateParams = Omit<FlashMessage, 'id' | 'createdAt' | 'type'>;
type FlashMessageOptionKeys = 'message' | 'title' | 'duration';
type FlashMessageOptions = Omit<FlashMessageCreateParams, FlashMessageOptionKeys>;

export const flashMessageAPI = {
    /** Add an error flash message */
    addError: (message: string, title?: string, duration?: number, options?: FlashMessageOptions): string =>
        flashMessageStoreActions.addMessage({
            type: ALERT_TYPES.ERROR,
            message,
            title,
            duration,
            ...options,
        }),
    /** Add a success flash message */
    addSuccess: (message: string, title?: string, duration?: number, options?: FlashMessageOptions): string =>
        flashMessageStoreActions.addMessage({
            type: ALERT_TYPES.SUCCESS,
            message,
            title,
            duration,
            ...options,
        }),
    /** Add a warning flash message */
    addWarning: (message: string, title?: string, duration?: number, options?: FlashMessageOptions): string =>
        flashMessageStoreActions.addMessage({
            type: ALERT_TYPES.WARNING,
            message,
            title,
            duration,
            ...options,
        }),
    /** Add an info flash message */
    addInfo: (message: string, title?: string, duration?: number, options?: FlashMessageOptions): string =>
        flashMessageStoreActions.addMessage({
            type: ALERT_TYPES.INFO,
            message,
            title,
            duration,
            ...options,
        }),

    /** Add a flash message with custom type */
    add: (params: Omit<FlashMessage, 'id' | 'createdAt'>): string => flashMessageStoreActions.addMessage(params),

    /** Dismiss a specific flash message */
    dismiss: (id: string): void => flashMessageStoreActions.removeMessage(id),

    /** Clear all flash messages */
    clearAll: (): void => flashMessageStoreActions.clearAllMessages(),

    /** Get all current messages (snapshot) */
    getMessages: (): ReadonlyArray<FlashMessage> => flashMessageStoreActions.getMessages(),

    /** Get messages observable for reactive subscriptions */
    getMessagesObservable: () => flashMessageStoreActions.getMessagesObservable(),
} as const;

export type FlashMessageAPI = typeof flashMessageAPI;
